import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, ArrowRight, ZoomIn, ZoomOut, X } from 'lucide-react';
import { optimizeContentfulImage } from '@/utils/imageUtils';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";

interface ImageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: string[];
  currentIndex: number;
  onPrevImage: (e: React.MouseEvent) => void;
  onNextImage: (e: React.MouseEvent) => void;
}

const ImageModal: React.FC<ImageModalProps> = ({
  open,
  onOpenChange,
  images,
  currentIndex,
  onPrevImage,
  onNextImage
}) => {
  
  const [imageUrl, setImageUrl] = useState('');
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [animating, setAnimating] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const programmaticRef = useRef(false);

  // Check if the device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Update image and thumbnails when modal opens or index changes
  useEffect(() => {
    if (open && images && images.length > 0 && currentIndex < images.length) {
      // Use a high-resolution version for the main image
      const mainImageUrl = images[currentIndex];
      setImageUrl(mainImageUrl);
      console.log(`Modal image URL: ${mainImageUrl}`);

      // Generate thumbnails for the gallery
      const thumbs = images.map(img => {
        if (img.includes('images.ctfassets.net')) {
          return optimizeContentfulImage(img, 200, 150);
        }
        return img;
      });
      setThumbnails(thumbs);

      // Reset swipe state
      setSwipeDirection(null);
      setSwipeProgress(0);
      setAnimating(false);

      // Prevent body scrolling while modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scrolling when modal closes
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, images, currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'ArrowLeft') {
        onPrevImage(e as unknown as React.MouseEvent);
      } else if (e.key === 'ArrowRight') {
        onNextImage(e as unknown as React.MouseEvent);
      } else if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onPrevImage, onNextImage, onOpenChange]);

  // Sync carousel with external currentIndex on mobile
  useEffect(() => {
    if (!isMobile || !carouselApi) return;
    const selected = carouselApi.selectedScrollSnap();
    if (selected !== currentIndex) {
      programmaticRef.current = true;
      carouselApi.scrollTo(currentIndex);
    }
  }, [currentIndex, carouselApi, isMobile]);

  // Reflect user swipes back to parent index change on mobile
  useEffect(() => {
    if (!isMobile || !carouselApi) return;
    const onSelect = () => {
      const newIndex = carouselApi.selectedScrollSnap();
      if (programmaticRef.current) {
        programmaticRef.current = false;
        return;
      }
      if (newIndex === currentIndex) return;
      if (newIndex > currentIndex) {
        onNextImage({ stopPropagation: () => {} } as React.MouseEvent);
      } else {
        onPrevImage({ stopPropagation: () => {} } as React.MouseEvent);
      }
    };
    carouselApi.on('settle', onSelect);
    return () => {
      carouselApi.off('settle', onSelect);
    };
  }, [carouselApi, currentIndex, onNextImage, onPrevImage, isMobile]);

  // Enhanced touch handlers for smoother swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setAnimating(false);
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);
    const diff = touchStart - currentTouch;
    const screenWidth = window.innerWidth;
    const percentMoved = Math.min(Math.abs(diff) / (screenWidth * 0.5), 1);

    // Determine swipe direction and progress
    if (diff > 0) {
      setSwipeDirection('left');
    } else {
      setSwipeDirection('right');
    }
    setSwipeProgress(percentMoved);

    // Apply transform to the image for drag effect
    if (imageRef.current) {
      const translateX = -diff * 0.5; // Make drag effect less extreme
      const opacity = 1 - percentMoved * 0.3; // Slight fade as you drag
      imageRef.current.style.transform = `translateX(${translateX}px)`;
      imageRef.current.style.opacity = opacity.toString();
    }
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const threshold = window.innerWidth * 0.2; // 20% of screen width
    const isSignificantSwipe = Math.abs(distance) > threshold;

    // Reset image styling
    if (imageRef.current) {
      imageRef.current.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
      imageRef.current.style.transform = 'translateX(0)';
      imageRef.current.style.opacity = '1';
    }
    setAnimating(true);

    // If swipe is significant enough, trigger the image change
    if (isSignificantSwipe) {
      if (distance > 0) {
        // Left swipe - next image
        onNextImage({
          stopPropagation: () => {}
        } as React.MouseEvent);
      } else {
        // Right swipe - previous image
        onPrevImage({
          stopPropagation: () => {}
        } as React.MouseEvent);
      }
    }

    // Reset values after animation completes
    setTimeout(() => {
      setSwipeDirection(null);
      setSwipeProgress(0);
      setAnimating(false);
      if (imageRef.current) {
        imageRef.current.style.transition = '';
      }
    }, 300);
    setTouchStart(null);
    setTouchEnd(null);
  };

  if (!open || !images || images.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent disableAnimations className="max-w-[95vw] max-h-[95vh] p-0 border-none overflow-hidden bg-transparent">
        {/* DialogTitle for accessibility but hidden visually */}
        <DialogTitle className="sr-only">Image Gallery</DialogTitle>
        
        <div className="relative w-full h-full flex flex-col bg-transparent">
          {/* Main image display area */}
          <div className="flex-1 relative overflow-hidden">
            {isMobile ? (
              <Carousel
                setApi={(api) => setCarouselApi(api)}
                className="w-full h-full"
                opts={{
                  loop: false,
                  dragFree: false,
                  containScroll: 'trimSnaps',
                  skipSnaps: false,
                  align: 'start',
                  startIndex: currentIndex
                }}
              >
                <CarouselContent className="h-full -ml-0">
                  {images.map((img, idx) => (
                    <CarouselItem key={idx} className="h-full pl-0 basis-full">
                      <div className="flex h-full items-center justify-center">
                        <img
                          src={img}
                          alt={`Gallery image ${idx + 1} of ${images.length}`}
                          className="max-w-full max-h-[80vh] w-auto object-contain"
                          onError={(e) => {
                            console.error(`Failed to load modal image: ${img}`)
                            if (img.includes('?')) {
                              e.currentTarget.src = img.split('?')[0]
                            }
                          }}
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            ) : (
              <div className="transition-all duration-300 h-full flex items-center justify-center">
                {imageUrl && (
                  <img
                    ref={imageRef}
                    src={imageUrl}
                    alt={`Image ${currentIndex + 1}`}
                    className="max-w-full max-h-[80vh] w-auto object-contain transition-transform"
                    style={{
                      transition: animating ? 'transform 0.3s ease-out, opacity 0.3s ease-out' : 'none',
                    }}
                    onError={(e) => {
                      console.error(`Failed to load modal image: ${imageUrl}`)
                      if (imageUrl.includes('?')) {
                        e.currentTarget.src = imageUrl.split('?')[0]
                      }
                    }}
                  />
                )}
              </div>
            )}

            {/* Navigation arrows - only show on desktop */}
            {!isMobile && <>
                <button onClick={onPrevImage} aria-label="Previous image" className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-colors z-10 bg-zinc-800 hover:bg-zinc-700">
                  <ArrowLeft size={24} className="text-white" />
                </button>
                
                <button onClick={onNextImage} aria-label="Next image" className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-colors z-10 bg-zinc-800 hover:bg-zinc-700">
                  <ArrowRight size={24} className="text-white" />
                </button>
              </>}

            {/* Only show close button on desktop */}
            {!isMobile && <button onClick={() => onOpenChange(false)} aria-label="Close" className="absolute right-4 top-4 p-2 rounded-full transition-colors z-10 bg-zinc-800 hover:bg-zinc-700">
                <X size={24} className="text-white" />
              </button>}
          </div>

          {/* Add swipe indicators for mobile */}
          {isMobile && <div className="absolute bottom-8 left-0 right-0 flex justify-center items-center gap-2 z-20">
              
            </div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageModal;
